import { formatDisplayEmail } from '@bs/core'
import { Button, pantryColors } from '@pantry/ui'
import React from 'react'
import type { FlowHistoryStatus } from '../../../../types/loyaltyMarketing'
import { useLoyaltyFlowHistoryQuery } from '../../hooks/useLoyaltyMarketing'
import { ActivityMessage, ActivityRow } from './CampaignActivityList'
import { EMERALD } from './CampaignTypeIcon'

const p = pantryColors

const formatWhen = (datetime?: string): string => {
  if (!datetime) return '—'
  const parsed = new Date(datetime)
  if (Number.isNaN(parsed.getTime())) return datetime
  return parsed.toLocaleString()
}

// sent = green, failed = red, scheduled/skipped = muted.
const statusColor = (status: FlowHistoryStatus): string => {
  if (status === 'sent') return EMERALD
  if (status === 'failed') return p.red[700]
  return p.gray[400]
}

const STATUS_TEXT: Record<FlowHistoryStatus, string> = {
  scheduled: 'Scheduled',
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
}

// Flow trigger log (scheduled → sent/failed/skipped per customer), newest
// first, "Load more" over the cursor feed. Skip reasons (converted,
// min_cart_value, unsubscribed — spec §4) show on the second line.
export const FlowActivityList: React.FC<{
  storeId: string
  flowId: string
  enabled?: boolean
}> = ({ storeId, flowId, enabled = true }) => {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLoyaltyFlowHistoryQuery(storeId, flowId, { enabled })
  const events = (data?.pages ?? []).flatMap((page) => page.events)

  if (isLoading)
    return <ActivityMessage loading>Loading history…</ActivityMessage>
  if (isError)
    return (
      <ActivityMessage>
        Couldn't load flow history. Please try again.
      </ActivityMessage>
    )
  if (events.length === 0)
    return <ActivityMessage>No triggers yet.</ActivityMessage>

  return (
    <div>
      {events.map((event) => (
        <ActivityRow
          key={event.id}
          color={statusColor(event.status)}
          text={`${STATUS_TEXT[event.status] ?? event.status} · ${
            formatDisplayEmail(event.email) || event.customer_id
          }`}
          sub={event.reason || undefined}
          when={formatWhen(event.created_at)}
        />
      ))}
      {hasNextPage && (
        <div
          style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}
        >
          <Button
            variant="ghost"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}
